/**
 * Öffentliche Marketing-Landingpage /partner-werden.
 * Eingebunden mit Header (vorhandener) wäre overkill — die Seite steht
 * standalone analog zu anderen Landingpages und nutzt nur den Footer.
 */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Repeat,
  ShieldCheck,
  LineChart,
  Megaphone,
  Banknote,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Footer from "@/components/Footer";
import BecomePartnerDialog from "@/components/partner-landing/BecomePartnerDialog";
import PartnerEarningsCalculator from "@/components/partner-landing/PartnerEarningsCalculator";
import LiveDashboardPreview from "@/components/partner-landing/LiveDashboardPreview";
import logo from "@/assets/musikonzept-logo.png";
import partnerHero from "@/assets/partner-hero.png";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMyAffiliate } from "@/hooks/useMyAffiliate";
import { SEIT_TEXT } from "@/config/webseiteFakten";

const benefits = [
  { icon: Repeat, title: "Du verdienst immer wieder", text: "20 % auf jede Zahlung deines Empfohlenen – Monat für Monat, solange seine Mitgliedschaft läuft." },
  { icon: ShieldCheck, title: "Wir merken uns deine Empfehlung", text: "Bis zu 60 Tage, auch wenn jemand erst später kauft. Oder dein Empfohlener gibt einfach deinen Namen bzw. Code im Bestellfeld an." },
  { icon: LineChart, title: "Eigener Bereich für dich", text: "Du siehst jederzeit, wie viele Leute auf deinen Link geklickt haben und was du schon verdient hast." },
  { icon: Megaphone, title: "Wir geben dir alles an die Hand", text: "Fertige Texte, Bilder und einen QR-Code, den du z. B. in deiner Kita aufhängen kannst." },
  { icon: Banknote, title: "Auszahlung per Überweisung", text: "Ab 25 € Guthaben überweisen wir dir dein Geld aufs Konto." },
  { icon: Sparkles, title: "Sicher und fair", text: "Nach 30 Tagen ist deine Provision endgültig dein Geld – die Wartezeit gibt es nur, falls jemand sein Geld doch zurück haben möchte." },
];

const steps = [
  { num: "1", title: "Kostenlos anmelden", text: "Such dir einen kurzen, persönlichen Code aus – fertig. Kein langes Formular." },
  { num: "2", title: "Deinen Link teilen", text: "Empfehle MusiKonzept persönlich, per WhatsApp, E-Mail oder über deine Social-Media-Kanäle." },
  { num: "3", title: "Geld verdienen", text: "Für jede Mitgliedschaft, die über dich entsteht, bekommst du 20 % Provision – jeden Monat aufs Neue." },
];

const faqs = [
  { q: "Wer darf mitmachen?", a: "Jede und jeder, der Lust hat, MusiKonzept weiterzuempfehlen. Du brauchst kein Gewerbe, kein Vorwissen und keine eigene Website." },
  { q: "Wann bekomme ich mein Geld?", a: "Wir warten ab jeder Zahlung deines Empfohlenen 30 Tage, damit klar ist, dass sie endgültig ist. Ab 25 € Guthaben überweisen wir dir dein Geld aufs Konto." },
  { q: "Wie funktioniert das Empfehlen genau?", a: "Du bekommst von uns einen persönlichen Link und einen kurzen Code. Wer über deinen Link kommt oder deinen Code bei der Bestellung eingibt, wird dir zugeordnet – und du bekommst deine Provision." },
  { q: "Was, wenn jemand nicht sofort kauft?", a: "Kein Problem. Bis zu 60 Tage lang merkt sich unsere Seite die Empfehlung. Kauft die Person später, bekommst du trotzdem deine Provision." },
  { q: "Muss ich das versteuern?", a: "Ja, deine Provisionen sind Einnahmen, die du beim Finanzamt angibst. Eine Steuernummer reicht – auch als Kleinunternehmer:in." },
];

const Header = ({ onCta }: { onCta: () => void }) => (
  <header className="sticky top-0 z-40 border-b border-border/40 bg-background/80 backdrop-blur">
    <div className="container mx-auto flex items-center justify-between px-6 py-3">
      <a href="/" className="flex items-center gap-2">
        <img src={logo} alt="MusiKonzept" className="h-9 w-auto" />
      </a>
      <Button onClick={onCta} size="sm">Partner werden</Button>
    </div>
  </header>
);

const PartnerLanding = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const { data: affiliate } = useMyAffiliate(user?.id);

  // Nach Login/Registrierung: Dialog automatisch wieder öffnen (?intent=open)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("intent") !== "open") return;
    if (affiliate?.status === "active") {
      navigate("/partner", { replace: true });
      return;
    }
    setDialogOpen(true);
    // Query-Param entfernen, damit ein späteres Schließen nicht erneut auto-öffnet
    const url = new URL(window.location.href);
    url.searchParams.delete("intent");
    window.history.replaceState({}, "", url.toString());
  }, [user?.id, affiliate?.status, navigate]);

  const openCta = () => {
    if (affiliate?.status === "active") {
      navigate("/partner");
      return;
    }
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Partnerprogramm – MusiKonzept empfehlen & verdienen</title>
        <meta
          name="description"
          content="Werde MusiKonzept-Partner: 20 % Provision auf jede Mitgliedschaft und jeden Kurs deiner Empfehlungen. Faires Tracking, eigenes Dashboard, SEPA-Auszahlung ab 25 €."
        />
        <link rel="canonical" href="https://www.musikonzept.de/partner-werden" />
        <meta property="og:title" content="Partnerprogramm – MusiKonzept" />
        <meta property="og:description" content="20 % Provision auf jede Empfehlung. Jetzt kostenlos Partner werden." />
        <meta property="og:url" content="https://www.musikonzept.de/partner-werden" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "MusiKonzept Partnerprogramm",
          provider: { "@type": "Organization", name: "MusiKonzept" },
          description: "Affiliate-Programm mit 20 % wiederkehrender Provision.",
        })}</script>
      </Helmet>

      <Header onCta={openCta} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto grid gap-12 px-6 py-20 lg:grid-cols-2 lg:items-center lg:py-28">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Neu: MusiKonzept Partnerprogramm
            </div>
            <h1 className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Empfehle MusiKonzept –
              <span className="block bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                verdiene mit.
              </span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">
              Bekomme <strong>20 % Provision</strong> auf jede Mitgliedschaft, die du uns empfiehlst –
              jeden Monat aufs Neue, solange dein Empfohlener dabei bleibt.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={openCta} className="text-base">
                Jetzt Partner werden <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" asChild className="text-base">
                <a href="#so-funktionierts">So funktioniert&apos;s</a>
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                ["20 %", "Provision pro Empfehlung"],
                ["bis zu 60 Tage", "merken wir uns deine Empfehlung"],
                ["sicher & fair", "geprüfte Provisionen"],
                ["ab 25 €", "Auszahlung aufs Konto"],
              ].map(([big, small]) => (
                <div key={big} className="rounded-xl border bg-card/60 p-3 text-center backdrop-blur">
                  <div className="text-base font-bold text-primary sm:text-lg">{big}</div>
                  <div className="mt-1 text-xs leading-tight text-muted-foreground">{small}</div>
                </div>
              ))}
            </div>
          </div>

          <LiveDashboardPreview />
        </div>
      </section>

      {/* STEPS */}
      <section id="so-funktionierts" className="bg-gradient-to-br from-primary/5 via-background to-accent/10 py-20">
        <div className="container mx-auto px-6">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold sm:text-4xl">So funktioniert&apos;s</h2>
            <p className="mt-2 text-muted-foreground">In 3 Minuten startklar.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((s, i) => {
              const bg = i === 1 ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground";
              return (
                <div key={s.num} className="rounded-2xl border bg-card p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md">
                  <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full font-bold ${bg}`}>
                    {s.num}
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <PartnerEarningsCalculator />

      {/* SHOWCASE mit Bild */}
      <section className="relative overflow-hidden bg-gradient-to-br from-accent/20 via-background to-primary/15 py-20">
        <div className="absolute -left-24 top-12 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -right-24 bottom-12 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />
        <div className="container relative mx-auto grid gap-12 px-6 lg:grid-cols-2 lg:items-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-primary/30 to-accent/30 blur-3xl" />
            <img
              src={partnerHero}
              alt="Zwei Aquarell-Silhouetten reichen sich Herz und Musiknote weiter"
              width={1024}
              height={1024}
              loading="lazy"
              className="relative mx-auto w-full max-w-lg"
            />
            <div className="absolute bottom-2 right-2 rounded-2xl bg-card/95 px-5 py-3 shadow-xl backdrop-blur sm:bottom-6 sm:right-6">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Empfehlung Nr. 12</div>
              <div className="text-base font-bold text-primary">+7,80 € / Monat 🎉</div>
            </div>
          </div>
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent-foreground">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> Empfehlen mit gutem Gefühl
            </div>
            <h2 className="text-3xl font-bold leading-tight sm:text-4xl">
              Du gibst etwas weiter, das wirklich hilft –
              <span className="block text-primary">und verdienst nebenbei mit.</span>
            </h2>
            <p className="mt-5 text-lg text-muted-foreground">
              MusiKonzept begleitet pädagogische Fachkräfte {SEIT_TEXT}. Wenn du andere darauf aufmerksam machst,
              hilfst du Kindern zu mehr Musik im Alltag – und bekommst von uns 20 % Provision als Dankeschön.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Kein Risiko, keine Kosten, keine Mindestlaufzeit",
                "Du entscheidest, wem du es empfehlen möchtest",
                "Wir kümmern uns um Bezahlung, Rechnung und Support",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <span className="text-sm">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHO */}
      <section className="container mx-auto px-6 py-20">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold sm:text-4xl">Ideal für dich, wenn du…</h2>
        </div>
        <div className="mx-auto grid max-w-4xl gap-3 sm:grid-cols-2">
          {[
            "…in einer Kita oder Krippe arbeitest und Kolleginnen MusiKonzept zeigen willst",
            "…als Tagesmutter oder Tagesvater Familien gute Materialien empfehlen möchtest",
            "…Fortbildungen oder Workshops für pädagogische Fachkräfte gibst",
            "…einen Blog, Instagram- oder Facebook-Kanal rund um Kita, Krippe oder Familie hast",
            "…als Elternbegleiterin oder Coach mit Familien arbeitest",
            "…einfach von MusiKonzept überzeugt bist und es weitersagen möchtest",
          ].map((line, i) => (
            <div
              key={line}
              className={`flex items-start gap-3 rounded-xl border p-4 ${
                i % 2 === 0 ? "bg-primary/5 border-primary/20" : "bg-accent/10 border-accent/30"
              }`}
            >
              <CheckCircle2 className={`mt-0.5 h-5 w-5 shrink-0 ${i % 2 === 0 ? "text-primary" : "text-accent"}`} />
              <span className="text-sm">{line}</span>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-gradient-to-b from-muted/40 to-primary/10 py-20">
        <div className="container mx-auto px-6">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold sm:text-4xl">Was dich erwartet</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => {
              const palette = [
                "bg-primary/15 text-primary",
                "bg-accent/20 text-accent",
                "bg-primary/15 text-primary",
                "bg-accent/20 text-accent",
                "bg-primary/15 text-primary",
                "bg-accent/20 text-accent",
              ];
              return (
                <div key={b.title} className="rounded-2xl border bg-card p-6 transition hover:-translate-y-1 hover:shadow-lg">
                  <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl ${palette[i % palette.length]}`}>
                    <b.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold">{b.title}</h3>
                  <p className="text-sm text-muted-foreground">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto max-w-3xl px-6 py-20">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold sm:text-4xl">Häufige Fragen</h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`}>
              <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="bg-gradient-to-br from-primary via-primary to-accent py-20 text-primary-foreground">
        <div className="container mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold sm:text-4xl">Bereit zum Mitmachen?</h2>
          <p className="mx-auto mt-3 max-w-xl text-primary-foreground/90">
            Erstelle in unter 2 Minuten kostenlos deinen Partner-Account und starte direkt mit dem Empfehlen.
          </p>
          <Button size="lg" variant="secondary" onClick={openCta} className="mt-8 text-base">
            Jetzt Partner werden <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />

      <BecomePartnerDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
};

export default PartnerLanding;
